"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { theme } from "@/styles/theme";

type Orientation = "horizontal" | "vertical";
type Difficulty = "easy" | "normal" | "hard";
type Mode = "play" | "loading";

type Props = {
  orientation: Orientation;
  onSolved: () => void;
  onCollision?: () => void;
  difficulty?: Difficulty;

  // NEW: visuals
  playerImgSrc?: string;          // e.g. /images/captcha/player.png
  goalImgSrc?: string;            // e.g. /images/captcha/objective.png
  goalLoadingImgSrc?: string;     // e.g. /images/captcha/objective-loading.gif

  // NEW: control state
  mode?: Mode;                    // "play" (default) | "loading"
  showHint?: boolean;
};

type Rect = { x: number; y: number; w: number; h: number };

type Obstacle = {
  id: string;
  rect: Rect;
  vx: number;
  vy: number;
};

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function circleIntersectsRect(cx: number, cy: number, r: number, rect: Rect) {
  const closestX = clamp(cx, rect.x, rect.x + rect.w);
  const closestY = clamp(cy, rect.y, rect.y + rect.h);
  const dx = cx - closestX;
  const dy = cy - closestY;
  return dx * dx + dy * dy <= r * r;
}

function circlesIntersect(
  ax: number,
  ay: number,
  ar: number,
  bx: number,
  by: number,
  br: number
) {
  const dx = ax - bx;
  const dy = ay - by;
  const rr = ar + br;
  return dx * dx + dy * dy <= rr * rr;
}

export function HumanCaptchaGame({
  orientation,
  onSolved,
  onCollision,
  difficulty = "normal",
  playerImgSrc,
  goalImgSrc,
  goalLoadingImgSrc,
  mode = "play",
  showHint = true,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Render-only state (changes rarely)
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [obstacleIds, setObstacleIds] = useState<string[]>([]);

  // DOM refs
  const playerElRef = useRef<HTMLDivElement | null>(null);
  const goalElRef = useRef<HTMLDivElement | null>(null);
  const obstacleElsRef = useRef<Record<string, HTMLDivElement | null>>({});

  // Simulation refs (no re-render)
  const PLAYER_R = 18;
  const GOAL_R = 18;
  const EDGE_MARGIN = 22;

  const playerPosRef = useRef({ x: 0, y: 0 });
  const desiredPlayerPosRef = useRef({ x: 0, y: 0 });
  const goalPosRef = useRef({ x: 0, y: 0 });
  const startPosRef = useRef({ x: 0, y: 0 });

  const obstaclesRef = useRef<Obstacle[]>([]);

  // Drag state refs
  const draggingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const grabOffsetRef = useRef({ x: 0, y: 0 });

  // Loop refs
  const solvedRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const lastTRef = useRef<number | null>(null);

  const cfg = useMemo(() => {
    const base = {
      obstacleCount: 3,
      speed: 90, // px/sec
      thickness: 18,
      minLen: 64,
      maxLen: 120,
      lanePadding: 56,
    };

    if (difficulty === "easy") return { ...base, obstacleCount: 2, speed: 65 };
    if (difficulty === "hard")
      return { ...base, obstacleCount: 4, speed: 130, thickness: 20 };
    return base;
  }, [difficulty]);

  // ResizeObserver for responsiveness
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setSize({ w: Math.floor(rect.width), h: Math.floor(rect.height) });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const applyPlayerTransform = () => {
    const el = playerElRef.current;
    if (!el) return;
    const p = playerPosRef.current;
    el.style.transform = `translate3d(${p.x - PLAYER_R}px, ${p.y - PLAYER_R}px, 0)`;
  };

  const applyGoalTransform = () => {
    const el = goalElRef.current;
    if (!el) return;
    const g = goalPosRef.current;
    el.style.transform = `translate3d(${g.x - GOAL_R}px, ${g.y - GOAL_R}px, 0)`;
  };

  const applyObstacleTransform = (o: Obstacle) => {
    const el = obstacleElsRef.current[o.id];
    if (!el) return;

    el.style.transform = `translate3d(${o.rect.x}px, ${o.rect.y}px, 0)`;

    if (el.dataset.sized !== "1") {
      el.style.width = `${o.rect.w}px`;
      el.style.height = `${o.rect.h}px`;
      el.dataset.sized = "1";
    }
  };

  // Reset drag if we enter loading mode
  useEffect(() => {
    if (mode !== "loading") return;
    draggingRef.current = false;
    pointerIdRef.current = null;
  }, [mode]);

  // Initialize positions + obstacles when size/orientation changes
  useEffect(() => {
    if (!size.w || !size.h) return;

    solvedRef.current = false;

    const w = size.w;
    const h = size.h;

    // Start + goal
    if (orientation === "horizontal") {
      const start = { x: EDGE_MARGIN + PLAYER_R, y: h * 0.5 };
      const goal = { x: w - EDGE_MARGIN - GOAL_R, y: h * 0.5 };
      startPosRef.current = start;
      playerPosRef.current = start;
      desiredPlayerPosRef.current = start;
      goalPosRef.current = goal;
    } else {
      const start = { x: w * 0.5, y: EDGE_MARGIN + PLAYER_R };
      const goal = { x: w * 0.5, y: h - EDGE_MARGIN - GOAL_R };
      startPosRef.current = start;
      playerPosRef.current = start;
      desiredPlayerPosRef.current = start;
      goalPosRef.current = goal;
    }

    // Obstacles
    const obstacles: Obstacle[] = [];
    const count = cfg.obstacleCount;

    if (orientation === "horizontal") {
      // Vertical movers distributed in X
      const laneLeft = EDGE_MARGIN + cfg.lanePadding;
      const laneRight = w - EDGE_MARGIN - cfg.lanePadding;
      const usableW = laneRight - laneLeft;

      for (let i = 0; i < count; i++) {
        const t = (i + 1) / (count + 1);
        const x = laneLeft + usableW * t;

        const len = cfg.minLen + Math.random() * (cfg.maxLen - cfg.minLen);
        const rect: Rect = {
          x: x - cfg.thickness * 0.5,
          y: h * 0.5 - len * 0.5,
          w: cfg.thickness,
          h: len,
        };

        const dir = Math.random() < 0.5 ? -1 : 1;
        const speed = cfg.speed * (0.75 + Math.random() * 0.6);

        obstacles.push({ id: `o_${i}`, rect, vx: 0, vy: dir * speed });
      }
    } else {
      // Horizontal movers distributed in Y
      const laneTop = EDGE_MARGIN + cfg.lanePadding;
      const laneBottom = h - EDGE_MARGIN - cfg.lanePadding;
      const usableH = laneBottom - laneTop;

      for (let i = 0; i < count; i++) {
        const t = (i + 1) / (count + 1);
        const y = laneTop + usableH * t;

        const len = cfg.minLen + Math.random() * (cfg.maxLen - cfg.minLen);
        const rect: Rect = {
          x: w * 0.5 - len * 0.5,
          y: y - cfg.thickness * 0.5,
          w: len,
          h: cfg.thickness,
        };

        const dir = Math.random() < 0.5 ? -1 : 1;
        const speed = cfg.speed * (0.75 + Math.random() * 0.6);

        obstacles.push({ id: `o_${i}`, rect, vx: dir * speed, vy: 0 });
      }
    }

    obstaclesRef.current = obstacles;
    setObstacleIds(obstacles.map((o) => o.id));

    requestAnimationFrame(() => {
      applyPlayerTransform();
      applyGoalTransform();
      for (const o of obstaclesRef.current) applyObstacleTransform(o);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    size.w,
    size.h,
    orientation,
    cfg.obstacleCount,
    cfg.speed,
    cfg.thickness,
    cfg.minLen,
    cfg.maxLen,
    cfg.lanePadding,
  ]);

  // Main loop: single RAF drives both obstacles and player position
  useEffect(() => {
    const tick = (ts: number) => {
      if (lastTRef.current == null) {
        lastTRef.current = ts;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const dt = Math.min(0.033, (ts - lastTRef.current) / 1000);
      lastTRef.current = ts;

      const w = size.w;
      const h = size.h;
      if (!w || !h) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Move obstacles (keep moving even in loading to keep the scene alive)
      const obstacles = obstaclesRef.current;
      const topBound = EDGE_MARGIN;
      const bottomBound = h - EDGE_MARGIN;
      const leftBound = EDGE_MARGIN;
      const rightBound = w - EDGE_MARGIN;

      for (const o of obstacles) {
        o.rect.x += o.vx * dt;
        o.rect.y += o.vy * dt;

        if (o.vx !== 0) {
          if (o.rect.x <= leftBound) {
            o.rect.x = leftBound;
            o.vx *= -1;
          } else if (o.rect.x + o.rect.w >= rightBound) {
            o.rect.x = rightBound - o.rect.w;
            o.vx *= -1;
          }
        }

        if (o.vy !== 0) {
          if (o.rect.y <= topBound) {
            o.rect.y = topBound;
            o.vy *= -1;
          } else if (o.rect.y + o.rect.h >= bottomBound) {
            o.rect.y = bottomBound - o.rect.h;
            o.vy *= -1;
          }
        }

        applyObstacleTransform(o);
      }

      // Only allow play interactions in play mode
      const canPlay = mode === "play";

      if (draggingRef.current && !solvedRef.current && canPlay) {
        const d = desiredPlayerPosRef.current;
        playerPosRef.current = d;
        applyPlayerTransform();

        const p = playerPosRef.current;

        // Collision
        for (const o of obstacles) {
          if (circleIntersectsRect(p.x, p.y, PLAYER_R, o.rect)) {
            const start = startPosRef.current;
            playerPosRef.current = start;
            desiredPlayerPosRef.current = start;
            applyPlayerTransform();

            draggingRef.current = false;
            pointerIdRef.current = null;
            onCollision?.();
            break;
          }
        }

        // Win
        const g = goalPosRef.current;
        if (circlesIntersect(p.x, p.y, PLAYER_R, g.x, g.y, GOAL_R)) {
          solvedRef.current = true;
          draggingRef.current = false;
          pointerIdRef.current = null;
          onSolved();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTRef.current = null;
    };
  }, [size.w, size.h, onSolved, onCollision, mode]);

  const setDesiredPlayerClamped = (x: number, y: number) => {
    const w = size.w;
    const h = size.h;
    desiredPlayerPosRef.current = {
      x: clamp(x, PLAYER_R, w - PLAYER_R),
      y: clamp(y, PLAYER_R, h - PLAYER_R),
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (mode !== "play") return;
    if (solvedRef.current) return;

    draggingRef.current = true;
    pointerIdRef.current = e.pointerId;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const p = playerPosRef.current;
    grabOffsetRef.current = { x: mx - p.x, y: my - p.y };

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (mode !== "play") return;
    if (!draggingRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const nx = mx - grabOffsetRef.current.x;
    const ny = my - grabOffsetRef.current.y;
    setDesiredPlayerClamped(nx, ny);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (pointerIdRef.current !== e.pointerId) return;
    draggingRef.current = false;
    pointerIdRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const goalSrc = mode === "loading" && goalLoadingImgSrc ? goalLoadingImgSrc : goalImgSrc;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: theme.colors.Black,
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {/* Goal */}
      <div
        ref={goalElRef}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: GOAL_R * 2,
          height: GOAL_R * 2,
          borderRadius: 999,
          border: goalSrc ? "none" : `2px solid ${theme.colors.Green}`,
          boxSizing: "border-box",
          opacity: 0.95,
          willChange: "transform",
          overflow: "hidden",
        }}
      >
        {goalSrc && (
          <img
            src={goalSrc}
            alt="Goal"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              imageRendering: "pixelated",
              pointerEvents: "none",
              display: "block",
            }}
          />
        )}
      </div>

      {/* Obstacles */}
      {obstacleIds.map((id) => (
        <div
          key={id}
          ref={(el) => {
            obstacleElsRef.current[id] = el;
          }}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            background: theme.colors.Green,
            boxShadow: `0 0 0 1px ${theme.colors.Black} inset`,
            willChange: "transform",
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Player */}
      <div
        ref={playerElRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: PLAYER_R * 2,
          height: PLAYER_R * 2,
          borderRadius: 999,
          background: playerImgSrc ? "transparent" : theme.colors.White,
          border: playerImgSrc ? "none" : `2px solid ${theme.colors.Green}`,
          boxSizing: "border-box",
          cursor: mode === "play" ? "grab" : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: theme.fonts.body,
          color: theme.colors.Black,
          fontSize: 12,
          willChange: "transform",
          overflow: "hidden",
          pointerEvents: mode === "play" ? "auto" : "none",
        }}
        title={mode === "play" ? "Drag me" : ""}
      >
        {playerImgSrc ? (
          <img
            src={playerImgSrc}
            alt="Player"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              imageRendering: "pixelated",
              pointerEvents: "none",
              display: "block",
            }}
          />
        ) : null}
      </div>

      {/* Hint */}
      {showHint && (
        <div
          style={{
            position: "absolute",
            left: 10,
            right: 10,
            bottom: 10,
            fontFamily: theme.fonts.body,
            fontSize: 12,
            color: theme.colors.Text,
            opacity: 0.75,
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          Drag the hero to save the princess.
        </div>
      )}
    </div>
  );
}
